import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { animate, useReducedMotion, type AnimationPlaybackControls } from 'motion/react';
import {
  UNIT_PATH_SEGMENT_ATTR,
  UNIT_PATH_SEGMENT_COMPLETED_ATTR,
  UNIT_PATH_SEGMENT_LABEL_ATTR,
  UNIT_PATH_SEGMENT_NAME_ATTR,
  UNIT_PATH_SEGMENT_REACHED_ATTR,
  UNIT_PATH_SEGMENT_TOTAL_ATTR,
} from '../hooks/useDashboardUnitPath';

// The skills-area container that holds the whole unit path (same selector BackToPositionButton /
// TowardsMarker use as their anchor root).
const SKILLS_CONTAINER_SELECTOR = '.paper-grid-bg-skills';
// The app's fixed header height (.nc_header min-height: 96px) — the top the unit header pins to,
// mirroring StickyHeaderOnScroll's `tw-top-24`.
const APP_HEADER_HEIGHT = 96;
// Extra gap below the header's bottom edge for the scroll-spy reading line, so a segment counts as
// "current" a touch after its top slides under the header rather than exactly at the seam. This is the
// floor for the line — see SPY_LINE_VIEWPORT_FRACTION.
const SPY_LINE_GAP = 8;
// The reading line proper, as a fraction of the viewport height. A line fixed just under the header
// reads as "late" on a tall window: the segment's marker has to travel the whole screen and tuck under
// the header before the label changes, by which point several of that segment's cards are already in
// view. Taking a fraction of the viewport instead means the marker is always at the same place on
// screen when the label flips, whatever the window height. Clamped below by the header line so the
// marker is never hidden behind the header at the moment it counts.
const SPY_LINE_VIEWPORT_FRACTION = 0.4;
// How far ABOVE the reading line a navigated-to segment's start is parked. The landing is measured
// against the very line the spy will use to judge it (see readingLine), so this is pure margin: the
// old 4px let a few pixels of post-scroll layout drift push the segment back under the line and hand
// the selection straight back to the previous one.
const SEGMENT_LANDING_CLEARANCE = 24;
// Browsers round the scroll position they actually apply, so a frame the navigation wrote can report
// back a fraction of a pixel off the value it asked for. Anything beyond this is the student.
const USER_SCROLL_TOLERANCE = 2;
// Apple-like deceleration, matching useScrollFocusUnitIntoView.
const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];
const SCROLL_DURATION = 0.6;

export interface UnitPathSegment {
  key: string;
  // Short label: "Foundations" | "Target N".
  label: string;
  // Target/checkpoint name for a path segment (e.g. "Equation"); "" for Foundations.
  name: string;
  completed: number;
  total: number;
  // Whether the unlocked frontier has reached this segment (its entry connector is not locked). True
  // for every segment up to the one holding the last non-locked connector, false beyond it.
  reached: boolean;
}

interface SpyState {
  segments: UnitPathSegment[];
  // Index into `segments` of the segment currently in view, or -1 when the path is not on screen.
  activeIndex: number;
}

const EMPTY_STATE: SpyState = { segments: [], activeIndex: -1 };

export interface UseActiveUnitPathSegmentParams {
  // The sticky header element; its bottom edge is the scroll-spy reading line (== the app header
  // height + the header's own height while pinned). Falls back to APP_HEADER_HEIGHT when absent.
  headerRef: RefObject<HTMLElement | null>;
}

export interface UseActiveUnitPathSegmentResult {
  // The ordered unit-path segments (Foundations, path-1, path-2, …) read from the DOM.
  segments: UnitPathSegment[];
  // Index of the segment currently in view, or -1 when the path is not on screen / not yet mounted.
  activeIndex: number;
  // The segment at `activeIndex`, or null when there is none. Resolved here so every consumer reads
  // the same "which segment am I on" answer instead of re-deriving it from segments + activeIndex.
  activeSegment: UnitPathSegment | null;
  // Smooth-scrolls the page so the given segment's start lands just below the pinned header.
  scrollToSegment: (key: string) => void;
  // Re-measures the header height used by the reading line. Call it when the header height changes for
  // reasons OTHER than the active segment (i.e. once the expand animation settles). Segment-driven
  // height changes must NOT call this, or the oscillation loop returns.
  remeasureHeader: () => void;
}

const sameSegments = (a: UnitPathSegment[], b: UnitPathSegment[]): boolean =>
  a.length === b.length &&
  a.every(
    (segment, index) =>
      segment.key === b[index].key &&
      segment.label === b[index].label &&
      segment.name === b[index].name &&
      segment.completed === b[index].completed &&
      segment.total === b[index].total &&
      segment.reached === b[index].reached,
  );

/**
 * The reading line, in viewport pixels: a fixed fraction down the viewport, but never above the
 * (possibly pinned) header's bottom edge, so the segment marker that trips it is always visible when
 * it does. `headerTop` is the header's TOP edge — invariant to the active segment (pinned →
 * APP_HEADER_HEIGHT; unpinned → its flow position) — and the height is the CACHED one, not the live
 * bottom, so a segment flip cannot move the line.
 *
 * Shared by the spy and by scrollToSegment: a navigated-to segment is parked against exactly the line
 * that will later decide whether it is active, instead of against a separately derived guess.
 */
const readingLine = (headerTop: number, cachedHeaderHeight: number): number =>
  Math.max(headerTop + cachedHeaderHeight + SPY_LINE_GAP, window.innerHeight * SPY_LINE_VIEWPORT_FRACTION);

/**
 * Tracks which unit-path segment is currently in view and lets the caller step between segments.
 * Segments are read from the DOM anchors stamped by useDashboardUnitPath (data-unit-path-segment*),
 * scoped to the skills-area container, so the header and the (sibling) path stay decoupled. Native
 * window scroll + rAF, matching the dashboard house style; a MutationObserver re-detects when the path
 * re-renders or mounts late. `scrollToSegment` reuses the animate + reduced-motion scroll of
 * useScrollFocusUnitIntoView.
 *
 * Call it through UnitPathSegmentProvider, never directly: every consumer must share ONE instance, or
 * they end up with independently drifting reading lines and disagree about the active segment.
 */
export const useActiveUnitPathSegment = ({
  headerRef,
}: UseActiveUnitPathSegmentParams): UseActiveUnitPathSegmentResult => {
  const [state, setState] = useState<SpyState>(EMPTY_STATE);
  const rafIdRef = useRef<number | null>(null);
  const controlsRef = useRef<AnimationPlaybackControls | null>(null);
  const shouldReduceMotion = useReducedMotion();
  // The header height used to place the reading line. Cached (not read live) so the line does not move
  // when the active segment resizes the header. Only refreshed on expand-settle (remeasureHeader) and
  // window resize.
  //
  // This freezes the LINE, which is only half of it: the header is sticky, so it still takes up space
  // in the flow and any height change of its own reflows the path below, moving the anchors the line is
  // compared against. That is the other half of the loop, and it is closed in the header itself —
  // UnitPathSegmentIndicator keeps a constant height across segments. Do not reintroduce
  // segment-dependent height there.
  const cachedHeaderHeightRef = useRef<number>(0);
  // Lets remeasureHeader trigger a recompute; assigned to the effect's throttled scheduler while active.
  const scheduleUpdateRef = useRef<() => void>(() => {});
  // While a scrollToSegment animation is running, the segment the student actually asked for. The spy
  // keeps reading the DOM, but the selection is pinned here: otherwise the header steps through every
  // segment the scroll flies over on the way, and the landing — which is what decides the final answer
  // — is the only reading that was ever meant to count. null = free-running.
  const latchedIndexRef = useRef<number | null>(null);
  // Scroll position the running navigation last wrote, or null when none is running. A scroll that
  // lands anywhere else is the student's, and theirs cancels the navigation.
  const navigationScrollRef = useRef<number | null>(null);

  const releaseLatch = useCallback((): void => {
    latchedIndexRef.current = null;
    scheduleUpdateRef.current();
  }, []);

  const remeasureHeader = useCallback((): void => {
    if (headerRef.current) {
      cachedHeaderHeightRef.current = headerRef.current.offsetHeight;
    }
    scheduleUpdateRef.current();
  }, [headerRef]);

  useEffect(() => {
    const update = (): void => {
      const container = document.querySelector(SKILLS_CONTAINER_SELECTOR);
      const anchors = container?.querySelectorAll(`[${UNIT_PATH_SEGMENT_ATTR}]`);
      if (!anchors || anchors.length === 0) {
        setState(current => (current.activeIndex === -1 && current.segments.length === 0 ? current : EMPTY_STATE));
        return;
      }

      // rect.top and window scroll are both in post-`zoom` viewport pixels (.unit-path-scale wraps
      // only the path), so no unscaling is needed.
      const headerTop = headerRef.current?.getBoundingClientRect().top ?? APP_HEADER_HEIGHT;
      const threshold = readingLine(headerTop, cachedHeaderHeightRef.current);

      // Document order === render order (Foundations, path-1, path-2, …); the active segment is the
      // deepest one whose top has scrolled above the line, defaulting to the first when none have.
      const segments: UnitPathSegment[] = [];
      let activeIndex = 0;
      anchors.forEach((anchor, index) => {
        segments.push({
          key: anchor.getAttribute(UNIT_PATH_SEGMENT_ATTR) ?? '',
          label: anchor.getAttribute(UNIT_PATH_SEGMENT_LABEL_ATTR) ?? '',
          name: anchor.getAttribute(UNIT_PATH_SEGMENT_NAME_ATTR) ?? '',
          completed: Number(anchor.getAttribute(UNIT_PATH_SEGMENT_COMPLETED_ATTR)) || 0,
          total: Number(anchor.getAttribute(UNIT_PATH_SEGMENT_TOTAL_ATTR)) || 0,
          reached: anchor.getAttribute(UNIT_PATH_SEGMENT_REACHED_ATTR) === 'true',
        });
        if (anchor.getBoundingClientRect().top <= threshold) {
          activeIndex = index;
        }
      });

      // `segments` keeps refreshing while latched (progress and reached flags must stay live); only
      // the selection is held.
      const resolvedIndex = latchedIndexRef.current ?? activeIndex;

      setState(current =>
        current.activeIndex === resolvedIndex && sameSegments(current.segments, segments)
          ? current
          : { segments, activeIndex: resolvedIndex },
      );
    };

    const scheduleUpdate = (): void => {
      if (rafIdRef.current !== null) {
        return;
      }
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        update();
      });
    };

    // Expose the throttled scheduler so remeasureHeader can trigger a recompute, and take a first
    // (collapsed) height measurement before the initial compute; the header refreshes it via
    // remeasureHeader once each expand/collapse animation settles.
    scheduleUpdateRef.current = scheduleUpdate;
    cachedHeaderHeightRef.current = headerRef.current?.offsetHeight ?? 0;

    // A scroll the student performs themselves cancels the navigation, the way a browser's own smooth
    // scrolling yields to the wheel: flying them to a destination they are visibly scrolling away from
    // is worse than stopping where they took over. Comparing against the position the animation last
    // wrote catches every way they can do it — wheel, trackpad, touch, keys, dragging the scrollbar —
    // where listening for input events would miss the scrollbar.
    const releaseLatchOnUserScroll = (): void => {
      const navigationScroll = navigationScrollRef.current;
      if (navigationScroll === null || Math.abs(window.scrollY - navigationScroll) <= USER_SCROLL_TOLERANCE) {
        return;
      }
      // Clear BEFORE stopping: motion flushes one last frame synchronously inside stop(), and that
      // frame would scroll the page back off the position the student just moved it to. Null first
      // means the flush hits the guard in onUpdate and does nothing.
      navigationScrollRef.current = null;
      controlsRef.current?.stop();
      releaseLatch();
    };

    const handleScroll = (): void => {
      releaseLatchOnUserScroll();
      scheduleUpdate();
    };

    // Correct on first paint, then follow scroll / resize.
    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Resize can change the header height (title wrap, zoom breakpoints), so re-measure the cache.
    window.addEventListener('resize', remeasureHeader);

    // Re-detect when the path re-renders (skill-state changes) or mounts late.
    // Observing document.body covers both the container appearing and its descendants changing; the
    // rAF throttle and the change-guarded setState keep this cheap.
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, { childList: true, subtree: true });

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', remeasureHeader);
      observer.disconnect();
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [headerRef, releaseLatch, remeasureHeader]);

  // Stop any in-flight scroll animation on unmount, and drop its latch with it: `stop()` does not run
  // the animation's onComplete, so the latch would otherwise outlive the scroll that set it.
  useEffect(
    () => () => {
      controlsRef.current?.stop();
      latchedIndexRef.current = null;
      navigationScrollRef.current = null;
    },
    [],
  );

  const scrollToSegment = useCallback(
    (key: string): void => {
      // Same container and same document order the spy reads, so the index used for the latch is the
      // index the spy would report for this segment.
      const container = document.querySelector(SKILLS_CONTAINER_SELECTOR);
      const anchors = Array.from(container?.querySelectorAll(`[${UNIT_PATH_SEGMENT_ATTR}]`) ?? []);
      const index = anchors.findIndex(anchor => anchor.getAttribute(UNIT_PATH_SEGMENT_ATTR) === key);
      if (index === -1) {
        return;
      }

      // Park the segment start SEGMENT_LANDING_CLEARANCE above the reading line it will be judged
      // against. The header is pinned at APP_HEADER_HEIGHT by the time the scroll settles.
      const landingLine = readingLine(APP_HEADER_HEIGHT, cachedHeaderHeightRef.current) - SEGMENT_LANDING_CLEARANCE;
      const top = Math.max(0, anchors[index].getBoundingClientRect().top + window.scrollY - landingLine);

      // Show the requested segment straight away rather than after the scroll settles.
      latchedIndexRef.current = index;
      scheduleUpdateRef.current();

      controlsRef.current?.stop();
      if (shouldReduceMotion) {
        // One jump, nothing in flight for the student to interrupt.
        navigationScrollRef.current = null;
        window.scrollTo({ top, behavior: 'auto' });
        releaseLatch();
        return;
      }
      // Seeded with where the animation starts, so a scroll arriving before its first frame is still
      // recognised as the student's.
      navigationScrollRef.current = window.scrollY;
      controlsRef.current = animate(window.scrollY, top, {
        duration: SCROLL_DURATION,
        ease: EASE_OUT_EXPO,
        onUpdate: value => {
          // A frame already scheduled when the student took over still runs after stop(), and writing
          // it would yank the page back from where they just scrolled to. Clearing the ref is what
          // ends the navigation; this makes any late frame a no-op.
          if (navigationScrollRef.current === null) {
            return;
          }
          navigationScrollRef.current = value;
          window.scrollTo(0, value);
        },
        onComplete: () => {
          navigationScrollRef.current = null;
          releaseLatch();
        },
      });
    },
    [releaseLatch, shouldReduceMotion],
  );

  return {
    segments: state.segments,
    activeIndex: state.activeIndex,
    activeSegment: state.activeIndex >= 0 ? state.segments[state.activeIndex] ?? null : null,
    scrollToSegment,
    remeasureHeader,
  };
};
